import { useEffect, useMemo } from 'react';
import { FlexContainer, useDeviceSize, useTimezone } from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useData } from '../../providers';
import { REFUSAL_REASON, RoutesName, StatusOperations } from '../../states';
import { documentValidationState } from '../../states/atoms';
import { flattenDeepObject } from '../../utils';
import { CardList, DataList } from '../cards/CardList';
import { GoBackHeader } from '../goBackHeader';
import { Banner } from './Banner';

export const Security = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatTimezone } = useTimezone();
  const { operationId } = useParams();

  const { isMobile } = useDeviceSize();
  const { idValidatedState: morphologicalValidation } = useData();
  const documentValidation = useRecoilValue(documentValidationState);

  useEffect(() => {
    if (!documentValidation) {
      navigate(RoutesName.notFound, { replace: true });
    }
  }, [documentValidation]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getValues = (values?: Record<string, unknown>): DataList[] | [] => {
    const data = flattenDeepObject(values, false);
    return values
      ? Object.entries(data).map(([title, value]) => ({
          title,
          value:
            '' + title === 'decisionTime' || title === 'acceptanceTime'
              ? formatTimezone(value as string)
              : String(value),
        }))
      : [];
  };

  const data: DataList[] | undefined = useMemo(() => {
    const dataList: DataList[] = [
      {
        title: 'Status',
        value: t(morphologicalValidation.status || ''),
        valueStatus:
          morphologicalValidation.status === StatusOperations.SUCCEEDED
            ? 'success400'
            : 'error400',
      },
      ...getValues(morphologicalValidation.data?.data),
    ];
    const reason = morphologicalValidation.data?.data.reason
      ? {
          title: REFUSAL_REASON,
          value: morphologicalValidation.data.data.reason,
          valueStatus: 'error400',
        }
      : null;

    if (reason) dataList.splice(1, 0, reason as DataList);

    return dataList;
  }, [morphologicalValidation.data]);

  return (
    <>
      <GoBackHeader
        route={generatePath(RoutesName.results, {
          operationId,
        })}
        linkLabel="Go back"
        title="Morphological"
        linkId="security_detail_back_button"
        idTitle="security_detail_title_text"
      />
      <Banner idDocument="security_document_type_title_text" />
      <FlexContainer
        columnGap={isMobile ? '0' : '0.8'}
        rowGap={isMobile ? '0.8' : '0'}
        as="section"
        flexDirection={isMobile ? 'column' : 'row'}
      >
        <CardList
          title={t('Test detail')}
          subTitle={'Tests'}
          data={data}
          columnsPerRow={
            isMobile ||
            (!morphologicalValidation.data && !morphologicalValidation.loading)
              ? '1'
              : '2'
          }
          loading={morphologicalValidation.loading}
          error={morphologicalValidation.error}
          showEmptyState
          testId="security_detail_security_section"
          idTitle="security_detail_security_section_title_text"
          idSubtitle="security_detail_security_section_description_text"
        />
      </FlexContainer>
    </>
  );
};
